/**
 * Repas de la journée — **priorité visuelle n°2 et n°3** (specs §6.2) :
 * repliable, affichant son propre sous-total et son objectif, avec un bouton
 * d'ajout accessible depuis chaque repas.
 *
 * ⚠️ Immuabilité (§4.1) : les macros et le libellé affichés ici viennent
 * **exclusivement** de `entry.macros` et `entry.label`, tels que le serveur les
 * a figés. Ne jamais aller chercher un nom d'aliment ailleurs pour « enrichir »
 * la ligne — ce serait rouvrir la porte que la règle ferme.
 *
 * Chaque repas affiche la **progression de ses quatre macros vers son objectif
 * propre**, celui snapshoté depuis le repas type du modèle. Même code couleur
 * que le bandeau de la journée, pour que le repère soit le même partout.
 *
 * ⚠️ Une entrée de type `recipe` est un **bloc groupé, repliable** (specs §6.5-4).
 * Ses lignes viennent de `entry.ingredients`, figées à l'ajout — jamais de la
 * recette source, qui a pu changer ou disparaître depuis (§4.5). Toucher le
 * libellé rouvre le bloc pour corriger les quantités.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REFONTE « NOURRI » — la `.meal-card` du prototype
 * ══════════════════════════════════════════════════════════════════════════
 *
 * En-tête (chevron · pastille colorée · nom · « ⋯ » · « + »), bloc de
 * progression kcal, trois mini-barres de macro, puis la liste des entrées
 * séparée par un filet. La hiérarchie établie aux retours de l'étape 10 — les
 * calories d'abord, en gros, les trois macros ensuite — est exactement celle du
 * prototype : rien à rouvrir.
 *
 * 🔴 **Deux écarts au prototype, tous deux pour la même raison.** La maquette
 * peint la **barre de kcal** et le **bouton « + »** dans la teinte décorative du
 * repas ; ici ils sont en `--brand`.
 *
 *   - `design-tokens.md` (principe 2) fixe les rôles : les calories portent
 *     `--brand`, et les teintes `--m-*` sont **décoratives**. Une barre de
 *     progression qui changerait de couleur d'une carte à l'autre ferait dire
 *     deux choses à un même objet — et c'est cette constance qui rend l'app
 *     lisible d'un coup d'œil ;
 *   - `ProgressBar` et `Button` n'exposent d'ailleurs pas ces teintes, et il
 *     aurait fallu élargir la bibliothèque pour les leur donner.
 *
 * L'identité du repas reste portée par la **pastille**, où la couleur est
 * décorative et doublée par le nom juste à côté — exactement son rôle.
 */

import { useState } from 'react';

import type { LogEntryDTO, MacroSet, MealDTO } from '../../../../shared/types';
import {
  Button,
  Card,
  GlyphApple,
  GlyphBolt,
  GlyphChevronRight,
  GlyphClose,
  GlyphCutlery,
  GlyphMoon,
  GlyphMore,
  GlyphPlus,
  GlyphSun,
  GlyphTrash,
  ProgressBar,
} from '../../components/nourri';
import { MacroFieldsRow, MacroGapNote } from '../../components/ui/MacroFields';
import { Hint, Sheet } from '../../components/ui/primitives';
import { fieldsOf, macrosOf, type MacroFields } from '../../lib/formFields';
import { useFinePointer } from '../../hooks/useFinePointer';
import { useSwipeToDelete } from '../../hooks/useSwipeToDelete';
import { MACRO_ROWS } from '../../lib/macros';
import { readMacro, remainingWord } from '../../lib/macroStatus';
import { mealTone, type MealTone } from './mealTone';

function g(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString('fr-FR');
}

function kcal(value: number): string {
  return Math.round(value).toLocaleString('fr-FR');
}

/** Le glyphe qui va avec la teinte — les cinq du prototype, dans son ordre. */
function MealGlyph({ tone }: { tone: MealTone }) {
  if (tone === 'preworkout') return <GlyphBolt />;
  if (tone === 'breakfast') return <GlyphSun />;
  if (tone === 'snack') return <GlyphApple />;
  if (tone === 'dinner') return <GlyphMoon />;
  return <GlyphCutlery />;
}

/**
 * Ce qui a été consommé — « 150 g », « 2 × 1 escalope (130 g) », « 1 sur 4
 * portions ». Demandé après usage : le journal disait *quoi*, jamais *combien*.
 *
 * ⚠️ Trois formes, parce que `portion_label` n'a pas le même contenu selon le
 * type (§4.5) :
 *
 *   - **recette** — le libellé figé porte **déjà** le nombre (« 2 sur 4
 *     portions »), donc on le rend tel quel ; un rendement au poids n'a pas de
 *     libellé et se lit « 350 g » ;
 *   - **aliment en portion nommée** — le libellé décrit **une** portion
 *     (« 1 escalope (130 g) ») et `quantity` en donne le nombre : il faut donc
 *     le préfixer au-delà de 1 ;
 *   - **saisie libre** — `quantity`/`unit` valent par convention 1 « portion »,
 *     un remplissage et non une mesure (étape 5). Afficher « 1 portion » sur
 *     chaque ligne de saisie libre serait du bruit : on n'affiche rien.
 *
 * ⚠️ Tout est lu sur l'entrée figée, jamais sur la fiche d'aliment (§4.1).
 */
function entryQuantityLabel(entry: LogEntryDTO): string | null {
  if (entry.type === 'quick') return null;
  if (entry.type === 'recipe') {
    return entry.portionLabel ?? `${g(entry.quantity)} ${entry.unit}`;
  }
  if (entry.portionLabel !== null) {
    return entry.quantity === 1
      ? entry.portionLabel
      : `${g(entry.quantity)} × ${entry.portionLabel}`;
  }
  return `${g(entry.quantity)} ${entry.unit}`;
}

/** « 32 P · 41 G · 12 L · 410 kcal » — la forme dense du prototype. */
function macroLine(macros: MacroSet): string {
  return `${g(macros.protein)} P · ${g(macros.carbs)} G · ${g(macros.fat)} L · ${kcal(macros.kcal)} kcal`;
}

/**
 * Une entrée du journal — **on la supprime en la glissant vers la gauche**,
 * comme une ligne de liste iOS native.
 *
 * 🔴 Demandé après essai sur l'appareil, en remplacement de la croix ronde qui
 * fermait chaque ligne (le prototype, lui, dessine un « ✕ »). Sur 393 px déjà
 * occupés par un libellé, une quantité et quatre macros, la croix coûtait 44 px
 * de largeur permanente pour un geste rare, et il fallait la viser.
 *
 * 🔴 **Au doigt seulement.** Sur un pointeur fin — souris, pavé tactile — la
 * croix reste au bout de la ligne et le glissement est désactivé : personne ne
 * devine qu'on peut faire glisser une ligne à la souris, alors qu'au doigt
 * c'est le geste de toutes les listes iOS. Les deux ne coexistent jamais
 * (`useFinePointer`).
 *
 * ⚠️ **Le bouton de suppression n'a pas disparu du DOM, il est passé derrière.**
 * Un geste ne se tabule pas et ne s'annonce pas : le panneau révélé est un vrai
 * `<button>`, toujours présent, toujours atteignable au clavier et par un
 * lecteur d'écran — et `nourri-screens.css` fait glisser la ligne quand il
 * prend la mise au point, pour que ce qui est activé soit aussi ce qui se voit.
 *
 * ⚠️ **Rien n'est demandé avant de supprimer**, ni ici ni avant : c'était déjà
 * le cas avec la croix. Le glissement complet est délibérément long (la moitié
 * de la largeur), et s'arrêter à mi-chemin ne fait qu'ouvrir le panneau.
 */
function EntryRow({
  entry,
  swiped,
  onSwipeOpen,
  onSwipeClose,
  onEdit,
  onRemove,
}: {
  entry: LogEntryDTO;
  /** Vrai si c'est cette ligne qui montre son panneau de suppression. */
  swiped: boolean;
  onSwipeOpen: () => void;
  onSwipeClose: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  // Une entrée provisoire (mise à jour optimiste, id négatif) n'est pas encore
  // modifiable : le serveur ne lui a pas encore attribué d'identifiant.
  const provisional = entry.id < 0;
  const isBlock = entry.type === 'recipe' && entry.ingredients.length > 0;
  // Bloc **replié par défaut** (specs §6.5-4) : le journal reste lisible d'un
  // coup d'œil, et déplier est un geste explicite.
  const [open, setOpen] = useState(false);
  const quantityLabel = entryQuantityLabel(entry);

  /*
   * 🔴 **Le geste au doigt, un bouton à la souris** — demandé après essai.
   * Glisser une ligne à la souris est un geste que personne ne devine, alors
   * qu'au doigt c'est celui de toutes les listes iOS. Les deux affordances ne
   * coexistent donc pas : sur un pointeur fin, la croix revient et le
   * glissement est **désactivé** (`enabled`), sinon un glissement à la souris
   * ouvrirait un panneau que rien n'annonce.
   */
  const fine = useFinePointer();

  const swipe = useSwipeToDelete({
    enabled: !provisional && !fine,
    open: swiped,
    onOpen: onSwipeOpen,
    onClose: onSwipeClose,
    onDelete: onRemove,
  });

  /**
   * Ce que fait un tap sur la ligne.
   *
   * ⚠️ **Trois cas, dans cet ordre.** Un clic qui n'est que la fin d'un
   * glissement ne doit rien déclencher ; sur une ligne ouverte, le tap
   * **referme** au lieu d'ouvrir la feuille de correction — c'est ce que fait
   * une liste iOS, et sans ça on ouvrirait un écran en voulant annuler son
   * geste.
   */
  const handleTap = (action: () => void) => () => {
    if (swipe.consumeTap()) return;
    if (swiped) {
      onSwipeClose();
      return;
    }
    action();
  };

  const contenu = ['n-swipe__content'];
  if (swipe.dragging) contenu.push('is-dragging');
  else if (swiped) contenu.push('is-open');

  return (
    /*
      🔴 **Le glissement n'enveloppe QUE la ligne, pas les ingrédients.**
      Relevé sur l'appareil : le panneau rouge occupait toute la hauteur du
      bloc déplié, si bien que son libellé — centré verticalement — s'affichait
      **sous** la ligne, à hauteur des ingrédients. Il déborde aussi
      permanemment de 10 px sous un bloc déplié, la marge basse de la liste
      d'ingrédients s'échappant du contenu par fusion de marges.

      La liste d'ingrédients est donc **sœur** du bloc glissant, dans le même
      `<li>`. Supprimer emporte toujours l'entrée entière ; c'est seulement son
      en-tête qui glisse.
    */
    <li className="n-entry">
      <div className="n-swipe" {...swipe.handlers}>
        {/*
          ⚠️ **Avant le contenu dans le DOM, et posé sous lui.** Ainsi la ligne
          n'a pas besoin d'être glissée pour que le bouton existe : il est
          simplement recouvert, et se découvre à mesure. C'est aussi ce qui rend
          la couleur rouge continue jusqu'au bord quelle que soit la course.

          ⚠️ Pas rendu du tout sur un pointeur fin : là, c'est la croix au bout
          de la ligne qui supprime, et deux boutons pour la même action se
          disputeraient l'annonce du lecteur d'écran.
        */}
        {!fine && (
          <button
            type="button"
            className="n-swipe__action"
            aria-label={`Supprimer ${entry.label}`}
            disabled={provisional}
            onClick={onRemove}
          >
            <GlyphTrash />
            Supprimer
          </button>
        )}

        <div
          className={contenu.join(' ')}
          /*
            🔴 **Un `transform` en ligne pendant le glissement SEULEMENT.** Au
            repos, la position vient d'une classe : un style en ligne gagnerait
            contre toute règle de feuille de style, et la ligne resterait fermée
            sous la mise au point du clavier (cf. `useSwipeToDelete`).
          */
          style={
            swipe.offset === null
              ? undefined
              : { transform: `translateX(${String(swipe.offset)}px)` }
          }
        >
          <div className={provisional ? 'n-food-row n-food-row--provisional' : 'n-food-row'}>
          {isBlock && (
            <button
              type="button"
              onClick={handleTap(() => {
                setOpen((value) => !value);
              })}
              aria-expanded={open}
              aria-label={open ? `Replier ${entry.label}` : `Déplier ${entry.label}`}
              className="n-meal__chevron"
            >
              <GlyphChevronRight size={14} />
            </button>
          )}

          <button
            type="button"
            onClick={handleTap(onEdit)}
            disabled={provisional}
            className="n-food-row__main"
          >
            <span className="n-food-row__name">
              {entry.label}
              {isBlock && (
                // Le nombre d'ingrédients dit que la ligne cache quelque chose,
                // même repliée — sinon rien ne distingue un bloc d'une entrée
                // ordinaire tant qu'on ne l'a pas ouvert.
                <span className="n-food-row__meta">
                  {' '}
                  · {String(entry.ingredients.length)} ingrédient(s)
                </span>
              )}
            </span>
            {/*
              Quantité et macros **sur la même ligne de méta**, comme le
              prototype (« 25 g · 20,5 P · 2 G · 0,5 L · 95 kcal »).

              ⚠️ La ligne est en `nowrap` avec ellipse : sur 393 px, une saisie
              au libellé long tronque proprement plutôt que de déborder.
            */}
            <span className="n-food-row__meta tnum">
              {quantityLabel !== null && `${quantityLabel} · `}
              {macroLine(entry.macros)}
            </span>
          </button>

            {/*
              La croix du prototype — **seulement sur un pointeur fin**. Au
              doigt, c'est le glissement qui supprime, et une croix permanente
              coûterait 44 px de largeur sur une ligne déjà dense.
            */}
            {fine && (
              <Button
                variant="secondary"
                shape="circle"
                aria-label={`Supprimer ${entry.label}`}
                disabled={provisional}
                onClick={onRemove}
              >
                <GlyphClose />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/*
        Les lignes du bloc, telles qu'elles ont été figées à l'ajout (§4.5).
        ⚠️ Aucune lecture de la recette ni des aliments : ce qui s'affiche ici
        est ce qui a été mangé ce jour-là, pas ce que la recette contient
        aujourd'hui.

        ⚠️ **Hors du bloc glissant** — cf. le commentaire du `<li>`. Supprimer
        emporte quand même l'entrée entière : c'est une seule ligne de journal,
        ses ingrédients n'ont pas d'existence séparée.
      */}
      {isBlock && open && (
        <ul className="n-ingredients">
          {entry.ingredients.map((line) => (
            <li key={line.id}>
              <span className="n-ingredients__name">{line.label}</span>
              <span className="n-ingredients__value tnum">
                {g(line.quantity)} {line.unit} · {kcal(line.macros.kcal)} kcal
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Bloc calories d'un repas — **la première chose qu'on lit sur une carte**.
 *
 * 🔴 Demandé après usage, et le prototype confirme : les kcal ont toute la
 * largeur, le chiffre en gros et leur barre ; les trois macros suivent sur la
 * ligne d'après.
 *
 * ⚠️ Même règle de couleur et même vocabulaire que le bandeau de la journée
 * (`lib/macroStatus.ts`) : rouge au delà de la tolérance, et jamais
 * « −0 kcal de trop ». Deux écrans qui diraient la même chose différemment
 * obligeraient à relire.
 *
 * ⚠️ Sans objectif propre — un repas ajouté à la main — on affiche le consommé
 * seul : il n'y a ni restant, ni progression à montrer, et une barre vide
 * laisserait croire à un objectif à zéro.
 */
function MealKcal({ meal }: { meal: MealDTO }) {
  const hasTarget = meal.targets.kcal > 0;
  const reading = readMacro(meal.remaining.kcal, meal.targets.kcal, 'kcal');

  return (
    <div className="n-meal__progress">
      <div className="n-meal__mp">
        <div className="n-meal__left tnum">
          {kcal(hasTarget ? Math.abs(reading.displayed) : meal.consumed.kcal)}{' '}
          <small>kcal {hasTarget ? remainingWord(reading) : 'au total'}</small>
        </div>

        {hasTarget && (
          <div className="n-meal__right tnum">
            {kcal(meal.consumed.kcal)} / {kcal(meal.targets.kcal)}
          </div>
        )}
      </div>

      {/*
        🔴 **La barre garde sa teinte de rôle, dépassée ou non.** Décidé après
        essai sur l'appareil : plus aucun rouge, ni sur le texte ni sur la
        jauge. Un journal bien rempli finissait constellé de barres rouges pour
        une situation ordinaire — dépasser une macro sur un repas n'est pas une
        erreur, et l'application décrit sans juger (specs §6.2). Le dépassement
        reste dit par le **mot** (« de trop ») et par les chiffres.
      */}
      {hasTarget && (
        <ProgressBar
          value={meal.consumed.kcal}
          max={meal.targets.kcal}
          tone="brand"
          label={`${meal.name} : ${kcal(Math.abs(reading.displayed))} kcal ${remainingWord(reading)}`}
        />
      )}
    </div>
  );
}

/** Les trois macros d'un repas — prototype `.meal-macro-mini`. */
function MealMacros({ meal }: { meal: MealDTO }) {
  return (
    <div className="n-meal__macros">
      {MACRO_ROWS.filter((row) => row.key !== 'kcal').map((row) => {
        const reading = readMacro(meal.remaining[row.key], meal.targets[row.key], row.unit);
        return (
          <div key={row.key}>
            <div className="n-mm__lbl">
              {row.short}{' '}
              <b className="tnum">
                {g(meal.consumed[row.key])}/{g(meal.targets[row.key])}
              </b>
            </div>
            <ProgressBar
              value={meal.consumed[row.key]}
              max={meal.targets[row.key]}
              /* Teinte d'identité, toujours — cf. `MealKcal`. */
              tone={row.key === 'kcal' ? 'brand' : row.key}
              size="xs"
              label={`${row.label} : ${g(Math.abs(reading.displayed))} ${row.unit} ${remainingWord(reading)}`}
            />
          </div>
        );
      })}
    </div>
  );
}

export function MealCard({
  meal,
  index,
  onAdd,
  onEditEntry,
  onRemoveEntry,
  onRename,
  onSetTargets,
  onRemove,
  onToggleFavorite,
}: {
  meal: MealDTO;
  /**
   * Position dans la journée — **décorative**, elle ne sert qu'à choisir la
   * teinte de la pastille quand le nom du repas ne la donne pas
   * (`mealTone.ts`).
   */
  index: number;
  onAdd: () => void;
  onEditEntry: (entry: LogEntryDTO) => void;
  onRemoveEntry: (entryId: number) => void;
  onRename: (name: string) => void;
  /** Les objectifs macros propres à ce repas (specs §6.2). */
  onSetTargets: (targets: MacroSet) => void;
  onRemove: () => void;
  /** Marque ce repas comme réutilisable depuis l'écran d'ajout. */
  onToggleFavorite: (isFavorite: boolean) => void;
}) {
  /*
   * 🔴 **Replié par défaut** — demandé après usage. Un journal de quatre repas
   * bien remplis dépliés d'office fait plusieurs écrans de haut : le bandeau de
   * restant et les jauges de chaque repas, qui sont ce qu'on vient lire (specs
   * §6.2), se retrouvaient noyés dans le détail des lignes. Déplier reste un
   * geste d'un tap, et les jauges du repas restent visibles replié.
   */
  const [open, setOpen] = useState(false);
  /*
   * 🔴 **Le menu « ⋯ » s'ouvre en feuille modale** — demandé après usage. Il
   * s'affichait auparavant *dans* la partie dépliée du repas : sur un repas
   * long, les options apparaissaient sous une dizaine de lignes, donc hors de
   * l'écran, et le bouton semblait sans effet.
   *
   * ⚠️ Une feuille plutôt qu'un menu ancré au bouton : elle est toujours au
   * même endroit quelle que soit la longueur du repas, elle réutilise les
   * gestes déjà éprouvés (glisser pour fermer, toucher le voile) et surtout
   * elle est **déjà déclarée dans `surfaces.css`** — une surface flottante
   * écrite pour l'occasion aurait dû l'être aussi, sous peine de réapparaître
   * par dessus les autres feuilles sur iPhone (défaut de l'étape 6).
   */
  const [managing, setManaging] = useState(false);
  /*
   * 🔴 **Un MODE, et non un booléen par formulaire.** La feuille a désormais
   * deux éditions — le nom et les objectifs — et deux drapeaux indépendants
   * pourraient être vrais en même temps : il faudrait alors décider lequel
   * gagne, c'est-à-dire tenir une règle de plus. Une union ne le peut pas.
   * Même raisonnement que `data-nudge` dans la coque : une seule condition
   * gouverne ce qui s'affiche.
   */
  const [mode, setMode] = useState<'menu' | 'rename' | 'targets'>('menu');
  const [draft, setDraft] = useState(meal.name);
  /*
   * ⚠️ **Semé au moment où l'on ouvre le formulaire**, jamais dans un effet.
   * Les cibles vivent en **chaînes** tant que le formulaire est ouvert
   * (`lib/formFields.ts`) : un champ doit pouvoir être vidé le temps d'une
   * frappe sans retomber à zéro sous les doigts.
   */
  const [targetDraft, setTargetDraft] = useState<MacroFields>(() => fieldsOf(meal.targets));
  /*
   * ⚠️ **Quelle entrée montre son panneau de suppression — un seul état pour
   * toute la carte.** Il vit ici et non dans chaque ligne : ouvrir une ligne
   * doit refermer la précédente, sans quoi on se retrouve avec trois boutons
   * rouges découverts et plus aucun moyen de savoir lequel on visait. C'est ce
   * que fait une liste iOS.
   */
  const [swiped, setSwiped] = useState<number | null>(null);

  const hasTarget =
    meal.targets.protein > 0 ||
    meal.targets.carbs > 0 ||
    meal.targets.fat > 0 ||
    meal.targets.kcal > 0;

  const tone = mealTone(meal.name, index);

  return (
    /*
      ⚠️ `padding="none"` : l'en-tête, le bloc de progression et la liste
      portent chacun leur propre gouttière, et le filet qui sépare les entrées
      doit toucher les deux bords de la carte — sinon il laisse une encoche
      carrée dans le rayon de 22 px.
    */
    <Card padding="none">
      <div className="n-meal__head">
        <button
          type="button"
          onClick={() => {
            setOpen((value) => !value);
          }}
          aria-expanded={open}
          aria-label={open ? `Replier ${meal.name}` : `Déplier ${meal.name}`}
          className="n-meal__chevron"
        >
          <GlyphChevronRight />
        </button>

        {/*
          Pastille du repas — couleur **décorative**, doublée par le nom juste à
          côté. Le glyphe est `aria-hidden` : il n'ajoute rien à ce que le nom
          dit déjà.
        */}
        <span className={`n-meal__icon n-meal__icon--${tone}`} aria-hidden="true">
          <MealGlyph tone={tone} />
        </span>

        {/*
          ⚠️ Le nom déplie lui aussi : c'est la plus grande cible de l'en-tête,
          et viser le chevron seul serait un piège. Les deux boutons portent
          donc le même `aria-expanded`, ce qui est correct — ils commandent le
          même état.
        */}
        <button
          type="button"
          onClick={() => {
            setOpen((value) => !value);
          }}
          aria-expanded={open}
          className="n-meal__title"
        >
          <span className="n-meal__name">{meal.name}</span>
          {/*
            ⚠️ Plus de rappel des kcal ici : le bloc juste en dessous les porte,
            en plus gros et avec sa barre. Ne reste que le cas « pas
            d'objectif », que le bloc ne peut pas dire.
          */}
          {!hasTarget && <span className="n-meal__notarget">sans objectif propre</span>}
        </button>

        <Button
          variant="ghost"
          shape="circle"
          aria-label={`Gérer ${meal.name}`}
          onClick={() => {
            // La feuille s'ouvre toujours sur son menu, jamais sur le
            // formulaire qu'on avait quitté la fois d'avant.
            setMode('menu');
            setDraft(meal.name);
            setManaging(true);
          }}
        >
          <GlyphMore />
        </Button>

        <Button
          variant="primary"
          shape="circle"
          onClick={onAdd}
          aria-label={`Ajouter à ${meal.name}`}
        >
          <GlyphPlus />
        </Button>
      </div>

      {/*
        Progression vers l'objectif **du repas** — celui snapshoté depuis le
        repas type du modèle. Affichée que le repas soit déplié ou non : c'est
        un état à lire d'un coup d'œil, au même titre que le bandeau de la
        journée (specs §6.2).
      */}
      <MealKcal meal={meal} />
      <MealMacros meal={meal} />

      {open && (
        <ul className="n-meal__items">
          {meal.entries.length === 0 ? (
            <li className="n-meal__empty">Rien d’enregistré pour l’instant.</li>
          ) : (
            meal.entries.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                swiped={swiped === entry.id}
                onSwipeOpen={() => {
                  setSwiped(entry.id);
                }}
                onSwipeClose={() => {
                  setSwiped((current) => (current === entry.id ? null : current));
                }}
                onEdit={() => {
                  onEditEntry(entry);
                }}
                onRemove={() => {
                  setSwiped(null);
                  onRemoveEntry(entry.id);
                }}
              />
            ))
          )}
        </ul>
      )}

      <Sheet
        open={managing}
        title={meal.name}
        onClose={() => {
          setManaging(false);
          setMode('menu');
        }}
      >
        {/*
          Renommer et supprimer n'ont aucun effet sur le modèle de journée
          source (specs §6.2) : ce sont des gestes sur **cette** journée.

          ⚠️ Actions **pleine largeur**, empilées, et non des petits boutons
          côte à côte : c'est la forme d'une feuille d'actions iOS, et c'est ce
          qui rend chaque cible large sans avoir à viser.
        */}
        {mode === 'rename' && (
          <div className="n-row">
            <input
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              className="ui-input"
              style={{ flex: 1 }}
              autoFocus
            />
            <Button
              variant="primary"
              shape="rounded"
              onClick={() => {
                if (draft.trim() !== '') onRename(draft.trim());
                setMode('menu');
                setManaging(false);
              }}
            >
              OK
            </Button>
          </div>
        )}

        {/*
          🔴 **Les objectifs d'un repas se règlent ICI** *(04/09/2026, demandé
          après usage : « dans le journal, fais en sorte que l'on puisse
          modifier les objectifs d'un repas »)*. Ils étaient jusque-là copiés du
          modèle à la création et **plus jamais modifiables** : un repas ajouté
          en cours de journée restait sans objectif pour toujours, et un
          objectif snapshoté d'un modèle qu'on avait fait évoluer restait faux
          jusqu'au lendemain.

          ⚠️ **Dans cette feuille, pas sur la carte.** Régler une cible est un
          geste rare — on le fait quand la journée s'organise autrement que
          prévu —, et la carte de repas porte déjà ce qu'on vient lire vingt
          fois par jour (specs §6.2). C'est le même arbitrage que pour Réglages
          sorti de la barre d'onglets : ne pas faire payer le geste quotidien
          pour le geste mensuel.

          ⚠️ **Le même formulaire que l'éditeur de modèles**, extrait et non
          recopié (`components/ui/MacroFields.tsx`) — les quatre macros restent
          indépendantes, l'écart 4/4/9 reste indicatif.
        */}
        {mode === 'targets' && (
          <div className="n-stack n-stack--tight">
            <MacroFieldsRow fields={targetDraft} onChange={setTargetDraft} />
            <MacroGapNote macros={macrosOf(targetDraft)} />

            {/*
              🔴 **Le total de la journée SUIT** *(04/09/2026, revirement
              demandé après usage : « changer les objectifs d'un repas met à
              jour les objectifs de la journée »)*. La veille, cette note disait
              l'inverse — le total était alors un snapshot figé à la création,
              et la phrase servait à ce que le silence ne passe pas pour un
              défaut. La migration `0013` a supprimé ce snapshot : la cible du
              jour **est** la somme de celles des repas, calculée à la lecture.
              La note reste, parce que la question se pose toujours ; c'est la
              réponse qui a changé.
            */}
            <Hint>
              Le total de la journée est la <b>somme</b> de ses repas : le bandeau suivra.
            </Hint>

            <Button
              variant="primary"
              shape="rounded"
              block
              onClick={() => {
                // Les quatre champs vidés remettent le repas sans objectif :
                // ses jauges réaffichent son total, ce qu'elles faisaient
                // avant qu'on lui en donne un. Effacer doit rester possible.
                onSetTargets(macrosOf(targetDraft));
                setMode('menu');
                setManaging(false);
              }}
            >
              Enregistrer les objectifs
            </Button>
          </div>
        )}

        {mode === 'menu' && (
          <div className="n-stack n-stack--tight">
            <Button
              variant="secondary"
              shape="rounded"
              block
              onClick={() => {
                setDraft(meal.name);
                setMode('rename');
              }}
            >
              Renommer
            </Button>

            <Button
              variant="secondary"
              shape="rounded"
              block
              onClick={() => {
                setTargetDraft(fieldsOf(meal.targets));
                setMode('targets');
              }}
            >
              Objectifs du repas
            </Button>

            {/*
              Mise en favori d'un repas — c'est ici, et nulle part ailleurs,
              qu'elle a sa place : c'est le seul écran où l'on voit ce que le
              repas contient. Un repas favori est proposé à la recopie depuis
              l'écran d'ajout, catégorie « Repas ».

              ⚠️ Désactivée sur un repas **vide** : recopier le vide n'a aucun
              sens, et le serveur ne le proposerait de toute façon pas.
            */}
            <Button
              variant="secondary"
              shape="rounded"
              block
              disabled={meal.entries.length === 0}
              onClick={() => {
                onToggleFavorite(!meal.isFavorite);
              }}
            >
              {meal.isFavorite ? '★ Repas favori' : '☆ Mettre en favori'}
            </Button>

            <Button
              variant="danger"
              shape="rounded"
              block
              onClick={() => {
                // La feuille se referme d'elle-même : le repas qu'elle décrit
                // n'existe plus, la laisser ouverte afficherait un titre sans
                // objet le temps de l'aller-retour.
                setManaging(false);
                onRemove();
              }}
            >
              Supprimer le repas
            </Button>
          </div>
        )}
      </Sheet>
    </Card>
  );
}
